/**
 * Marketing-site UI behavior autocapture.
 *
 * Same self-hosted posthog-js setup as the SPA's
 * src/web/src/modules/telemetry/ui-tracking.ts — points at /api/ui-events
 * on the same edge server so events land in the shared
 * ori_web_ui_events table.
 *
 * Distinguished from SPA events by the `source: 'marketing'` super
 * property (queryable via `payload->>'source'` on the table) and by the
 * page paths (`/`, `/about`, `/privacy`, etc. vs `/auth/...`, `/plan/...`).
 *
 * Marketing routing uses URL pathname (no client-side router), so each
 * page is a fresh load and the default $pageview/$pageleave cycle works
 * out of the box.
 */

import posthog from 'posthog-js';

import { getCookieConsent } from '../../../shared-web/cookie-consent';

const TRACKING_TOKEN_PLACEHOLDER = 'ori-marketing';
const TRACKING_ENDPOINT = '/api/ui-events';

let initialized = false;

export function initUiTracking(): void {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  // Opt-in consent gate: PostHog uses persistence 'localStorage+cookie', so
  // it drops a non-necessary cookie. Don't init until the visitor accepts.
  // The cookie banner re-calls this on Accept, so a returning visitor inits
  // on boot and a first-time visitor inits the moment they click Accept.
  if (getCookieConsent() !== 'accepted') return;

  // Skip the local Vite dev server (vite dev). Render-built marketing
  // bundles (both dev and prod env) run with import.meta.env.DEV === false
  // so this only short-circuits localhost:5173-style local runs.
  if (import.meta.env.DEV) return;

  posthog.init(TRACKING_TOKEN_PLACEHOLDER, {
    api_host: TRACKING_ENDPOINT,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    disable_surveys: true,
    advanced_disable_decide: true,
    advanced_disable_feature_flags: true,
    disable_compression: true,
    opt_out_useragent_filter: true,
    persistence: 'localStorage+cookie',
    person_profiles: 'identified_only',
    loaded: (ph) => {
      ph.register({
        source: 'marketing',
        route: window.location.pathname || '/',
      });
    },
  });

  initialized = true;
}
